// Declarative config for every Daily Field Report section. One generic row
// editor (SectionRowsStep) renders all of these; the same defs drive the
// review summary and totals footers, so computed columns live here once.
//
// Field types: text | multiline | number | select | time | bool | costCode
// Sections with typed: true save to their own SQL tables (PC.Field_Report_Labor
// / _Quantity); the rest go to PC.Field_Report_Section_Row as JSON.

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const sum = (rows, name) => rows.reduce((t, r) => t + num(r[name]), 0);

const round1 = (v) => Math.round(v * 10) / 10;

// A row the user never touched — the seeded starter rows every section opens
// with, or a row added and left empty. 'group' is structural on labor rows, so
// a blank crew/sub row still counts as blank.
export const isBlankRow = (r) => Object.entries(r || {}).every(([k, v]) =>
    k === 'group' || v == null || v === ''
    || (typeof v === 'object' && Object.keys(v).length === 0),
);

// The job's project type for the report header. Primary source is
// [Work_Type] / [Work_Type_Description] on PCV.Form_Job_Master — the job list
// queries alias Work_Type into the legacy Cat_Code_BU_18 slot, so the code
// ('CAP', 'NFC', …) is already on jobData; descriptions are mapped here until
// the job queries carry Work_Type_Description themselves. Falls back to the
// service-offering prefix parse over phase descriptions (the text before the
// first " - ", e.g. "Facility - Civil / Concrete") for jobs with no work type.
const WORK_TYPE_DESC = {
    SPS: 'Specialty Services',
    CPN: 'Capital Project New',
    MAN: 'Maintenance',
    PIP: 'Pipeline',
    PRM: 'Program Maintenance',
    'M&I': 'Maintenance & Integrity',
    PRN: 'Program New Build',
    CAF: 'Capital Facility',
    FAC: 'Facilities',
    NFC: 'New Facility Construction',
    CAP: 'Capital Pipeline',
    NPC: 'New Pipeline Construction',
    INT: 'Integrity',
    NEW: 'New Construction',
    'M&R': 'Maintenance & Repair',
    CPR: 'Capital Project Retrofit',
    OTH: 'Other',
    'E&U': 'Expansion & Upgrade',
    FAB: 'Fabrication',
};
const WORK_TYPE_RANK = ['pipeline', 'facility', 'facilities', 'e&i', 'engineering', 'cp', 'integrity', 'shop fab'];
export const jobWorkTypes = (jobData, costDetail, phaseCodes) => {
    const code = String(jobData?.Cat_Code_BU_18 || '').trim().toUpperCase();
    const desc = jobData?.Work_Type_Description || WORK_TYPE_DESC[code];
    if (desc) return [desc];
    const seen = new Map();
    (costDetail || []).forEach((row) => {
        const code = String(row?.Phase_Code || '').trim();
        const desc = String(row?.Phase_Code_Description || phaseCodes?.[code] || '');
        const idx = desc.indexOf(' - ');
        if (idx <= 0) return;
        const name = desc.slice(0, idx).trim();
        if (name && !seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
    });
    const rank = (n) => {
        const i = WORK_TYPE_RANK.indexOf(n.toLowerCase());
        return i < 0 ? WORK_TYPE_RANK.length : i;
    };
    return [...seen.values()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
};

export const sectionDefs = [
    {
        key: 'work',
        title: 'Work Performed',
        rowNoun: 'activity',
        // Picking a valid cost code on the last row auto-opens a fresh row so
        // the user can rattle off codes without tapping + Add each time.
        autoAddOnCostCode: true,
        fields: [
            { name: 'costCode', label: 'Cost Code', type: 'costCode' },
            { name: 'description', label: 'Description of Work Performed', type: 'multiline' },
        ],
    },
    {
        key: 'quantities',
        title: 'Quantities Installed / Reworked',
        rowNoun: 'quantity',
        typed: true,
        fields: [
            { name: 'costCode', label: 'Cost Code', type: 'costCode' },
            { name: 'task', label: 'Task', type: 'text' },
            { name: 'unit', label: 'Unit', type: 'text', placeholder: 'LF / EA / TON…' },
            { name: 'budgetQty', label: 'Budget Qty', type: 'number' },
            { name: 'installedToday', label: 'Installed Today', type: 'number' },
            { name: 'installedToDate', label: 'Installed To-Date', type: 'number' },
            { name: 'reworkedToday', label: 'Reworked Today', type: 'number' },
            { name: 'mhToday', label: 'MH Today', type: 'number' },
            {
                name: '_pct', label: '% Complete', computed: (r) =>
                    num(r.budgetQty) ? round1(num(r.installedToDate) * 100 / num(r.budgetQty)) + '%' : '—',
            },
            {
                name: '_prod', label: 'Prod. (Qty/MH)', computed: (r) =>
                    num(r.mhToday) ? round1(num(r.installedToday) / num(r.mhToday)) : '—',
            },
        ],
        totals: (rows) => [
            { label: 'Installed Today', value: sum(rows, 'installedToday') },
            { label: 'Reworked Today', value: sum(rows, 'reworkedToday') },
            { label: 'MH Today', value: sum(rows, 'mhToday') },
        ],
    },
    {
        key: 'labor',
        title: 'Labor & Crew On Site',
        rowNoun: 'crew',
        typed: true,
        // Table editor (LaborMatrix): rows = crews + subs. Row shape:
        // { group: 'crew'|'sub', name, discipline, headcount, hoursWorked, notes }.
        // Flattened to PC.Field_Report_Labor rows in buildSavePayload
        // (discipline → Activity column); regrouped on hydrate.
        matrix: true,
        fields: [],
        summarize: (r) => {
            const parts = [
                `${r.group === 'sub' ? 'Sub' : 'Crew'}: ${r.name || '—'}`,
            ];
            if (r.discipline) parts.push(r.discipline);
            parts.push(`Headcount: ${num(r.headcount)}`);
            if (num(r.hoursWorked)) parts.push(`${r.hoursWorked}h`);
            if (r.group === 'sub' && (r.percentComplete ?? '') !== '') parts.push(`${r.percentComplete}% complete`);
            if (r.group === 'sub' && r.notes) parts.push(r.notes);
            return parts.join(' · ');
        },
        totals: (rows) => {
            const crews = rows.filter((r) => r.group !== 'sub');
            const subs = rows.filter((r) => r.group === 'sub');
            const mh = rows.reduce((t, r) => t + num(r.headcount) * num(r.hoursWorked), 0);
            return [
                { label: 'Crew Headcount', value: sum(crews, 'headcount') },
                { label: 'Sub Headcount', value: sum(subs, 'headcount') },
                { label: 'Total On Site', value: sum(rows, 'headcount') },
                { label: 'Total MH', value: round1(mh) },
            ];
        },
    },
    {
        key: 'equipment',
        title: 'Equipment Used',
        rowNoun: 'equipment item',
        // Table editor (EquipmentMatrix): rows = units picked from the Burn
        // Tool asset list, on-the-job Burn Tool units, or rental PO lines.
        // Row shape: { equipment, unitNumber, firstOnJob, demobDate,
        // demobConfirmed } plus rental
        // context (rentalSupplier / rentalPo / rentalDue / rentalReturn) on
        // rows picked off a PO. Stored as JSON section rows, so no backend
        // change — the demob date is also kept job-level in
        // PC.Field_Report_Equipment_Dates so it carries onto later reports.
        matrixEquip: true,
        fields: [],
        summarize: (r) => {
            const parts = [[r.unitNumber, r.equipment].filter(Boolean).join(' — ') || '—'];
            if (num(r.quantity)) parts.push(`qty ${r.quantity}`);
            if (num(r.hoursUsed)) parts.push(`${r.hoursUsed}h`);
            if (num(r.downtimeHours)) parts.push(`${r.downtimeHours}h down`);
            const d = (iso) => `${parseInt(String(iso).slice(5, 7), 10)}/${parseInt(String(iso).slice(8, 10), 10)}`;
            if (r.rentalPo != null) {
                parts.push(`PO ${r.rentalPo}${r.rentalReturn ? ` returned ${d(r.rentalReturn)}` : ''}`);
            }
            if (r.demobDate) parts.push(`demob ${d(r.demobDate)}${r.demobConfirmed ? ' ✓' : ''}`);
            return parts.join(' · ');
        },
        // Hours and downtime are no longer entered here — the section is the
        // mob/demob record for the iron on the job, so the count is the total.
        totals: (rows) => [
            { label: 'Units', value: rows.filter((r) => !isBlankRow(r)).length },
        ],
    },
    {
        // Key stays 'delays' so existing draft rows keep loading; the section
        // is presented as the Issue Log.
        key: 'delays',
        title: 'Issue Log',
        rowNoun: 'issue',
        fields: [
            { name: 'type', label: 'Type of Issue', type: 'select', options: ['Weather', 'Third Party', 'Equipment', 'Material', 'Owner', 'Labor', 'Force Majeure', 'Other'] },
            { name: 'description', label: 'Description', type: 'multiline' },
            { name: 'durationHrs', label: 'Duration (hrs)', type: 'number' },
            { name: 'responsibleParty', label: 'Responsible Party', type: 'select', options: ['Contractor', 'Owner', 'Vendor', 'Sub'] },
            { name: 'subInvolved', label: 'Sub?', type: 'bool' },
            { name: 'potentialCO', label: 'Potential CO?', type: 'bool' },
            { name: 'clientNotified', label: 'Client Notified?', type: 'bool' },
        ],
        totals: (rows) => [
            { label: 'Duration (hrs)', value: sum(rows, 'durationHrs') },
        ],
    },
    {
        key: 'materials',
        title: 'Material Deliveries & Staging',
        rowNoun: 'delivery',
        // Card editor (MaterialDeliveryCards): card per delivery, supplier
        // first with vendor/PO suggestions from the job's open PO lines.
        deliveryCards: true,
        fields: [
            { name: 'paymentType', label: 'Payment', type: 'select', options: ['PO', 'Check Request', 'P-Card'] },
            { name: 'supplier', label: 'Supplier', type: 'vendor' },
            { name: 'poNumber', label: 'PO #', type: 'po' },
            { name: 'material', label: 'What Was Delivered', type: 'text' },
        ],
    },
    {
        // Subs merged into the Labor & Equipment step (LaborMatrix sub rows).
        // The section sticks around as derived storage: buildSavePayload
        // regenerates these rows from the labor sub rows so % complete
        // persists without a labor-table column; hidden keeps it out of the
        // review/sheet renderings.
        key: 'subs',
        title: 'Subcontractor Progress',
        rowNoun: 'subcontractor',
        hidden: true,
        fields: [
            { name: 'subcontractor', label: 'Subcontractor', type: 'text' },
            { name: 'scope', label: 'Scope', type: 'text' },
            { name: 'workPerformed', label: 'Work Performed Today', type: 'multiline' },
            { name: 'headcount', label: 'Headcount', type: 'number' },
            { name: 'hours', label: 'Hours', type: 'number' },
            { name: 'percentComplete', label: '% Complete', type: 'number', suffix: '%' },
            { name: 'onSchedule', label: 'On Schedule?', type: 'bool' },
            { name: 'notes', label: 'Issues / Notes', type: 'multiline' },
        ],
        totals: (rows) => [{ label: 'Headcount', value: sum(rows, 'headcount') }],
    },
    {
        // Pipeline quantity-module detail (QuantityCards): one JSON row per
        // tracked cost code — station pairs, crew days, per-size quantities,
        // comments — plus a '__meta' row holding the job's pipe-size list.
        // Every edit derives the flat typed 'quantities' rows from these, so
        // this section is storage + hydration only; review/sheet render the
        // derived rows.
        key: 'qtyDetail',
        title: 'Quantity Detail',
        rowNoun: 'quantity detail',
        hidden: true,
        fields: [],
    },
    {
        // Quick Report phase time card (HeavyJob-style): one row per crew
        // member, { name, hours: {costCode: hrs} }. buildSavePayload flattens
        // these to typed PC.Field_Report_Labor rows (one per person × phase,
        // CostCode set); on hydrate this JSON section stays the editing source
        // of truth and the CostCode-bearing labor rows are skipped by the
        // matrix regroup.
        key: 'timeCard',
        title: 'Phase Time Card',
        rowNoun: 'crew member',
        hidden: true,
        fields: [],
    },
    {
        // Quick Report (/daily-report2) totals — a single row of
        // { totalHours, totalQty, qtyUnit }. Stored as a JSON section so the
        // simplified wizard rides the standard save/read APIs with no backend
        // change; hidden keeps it off the full review/sheet renderings.
        key: 'simple',
        title: 'Quick Report Totals',
        rowNoun: 'entry',
        hidden: true,
        fields: [],
    },
    {
        // Caption/location edits for already-uploaded photos. There is no
        // update-photo API, so edits persist here (keyed by photoId) and the
        // display layer merges them over the uploaded values.
        key: 'photoMeta',
        title: 'Photo Notes',
        rowNoun: 'photo note',
        hidden: true,
        fields: [],
    },
    {
        key: 'safety',
        title: 'Safety',
        rowNoun: 'safety entry',
        fields: [
            // anyIncidents / incidentDetail are the Quick Report's whole safety
            // block ("Any safety incidents today?" → detail when Yes); the full
            // report's Setup tab keeps the longer list below.
            { name: 'anyIncidents', label: 'Any Safety Incidents?', type: 'bool' },
            { name: 'incidents', label: 'Safety Events', type: 'number' },
            { name: 'incidentDetail', label: 'Incident Detail', type: 'multiline' },
            { name: 'safetyRep', label: 'Safety Rep', type: 'text' },
            { name: 'safetyMeeting', label: 'Safety Meeting Held', type: 'bool' },
            { name: 'jsaReview', label: 'JSA Review', type: 'bool' },
            { name: 'sspEapReview', label: 'SSSP Reviewed', type: 'bool' },
            { name: 'eapKnown', label: 'Knows EAP', type: 'bool' },
            { name: 'safetyObservations', label: 'Safety Observations / Coaching', type: 'multiline' },
        ],
    },
    {
        // Yes/No/N/A activity checklist at the top of the Quality step
        // (QualityChecklist edits the single row in this section). Answering
        // Yes on an activity reveals its subQuestions; those answers store as
        // extra keys on the same row ('Y' / 'N' / 'NA') and clear when the
        // activity moves off Yes.
        key: 'quality',
        title: 'Quality Activities',
        rowNoun: 'entry',
        fields: [
            { name: 'civil', label: 'Civil', type: 'bool' },
            { name: 'ei', label: 'E&I', type: 'bool' },
            { name: 'mechanical', label: 'Mechanical', type: 'bool' },
            { name: 'nde', label: 'NDE', type: 'bool' },
            { name: 'coating', label: 'Coating', type: 'bool' },
            { name: 'welding', label: 'Welding', type: 'bool' },
            { name: 'pressureTesting', label: 'Pressure Testing', type: 'bool' },
        ],
        subQuestions: {
            civil: [
                { name: 'civilPourCard', label: 'Was the Pour Card completed?' },
                { name: 'civilBreakTest', label: 'Was the Break Test completed?' },
            ],
            ei: [
                { name: 'eiQualityForms', label: 'Were the appropriate Quality forms completed for each scope of work?' },
            ],
            mechanical: [
                { name: 'mechEquipmentSet', label: 'Is Equipment being set?' },
            ],
            nde: [
                { name: 'ndeRtMtPt', label: 'Is RT/MT/PT Complete?' },
                { name: 'ndeReport', label: 'Is NDE Report received?' },
                { name: 'ndeRejections', label: 'Any rejections received?' },
            ],
            coating: [
                { name: 'coatingReport', label: 'Was the Coating Report complete?' },
            ],
            welding: [
                { name: 'weldingTrackers', label: 'Were weld trackers submitted to Quality?' },
                { name: 'weldingRejections', label: 'Any rejections received?' },
            ],
            pressureTesting: [
                { name: 'ptTestingForms', label: 'All testing forms filled out and testing package complete?' },
            ],
        },
    },
    {
        // Concrete Pour Approval cards (digital STR-QUA-FM-07): one JSON row
        // per card, edited by the PourCards modal behind Civil = Yes on the
        // Quality checklist. Hidden keeps them off the generic row editor and
        // review — the modal owns rendering, and the PDF / SharePoint archive
        // / client-email actions post the card JSON to the pourCard endpoints.
        key: 'pourCards',
        title: 'Pour Cards',
        rowNoun: 'pour card',
        hidden: true,
        fields: [],
    },
    {
        // Item rows retired from the Quality step UI (checklist only now);
        // def stays so old drafts with inspection rows still persist.
        key: 'inspections',
        title: 'Inspections & Potential Change Events',
        rowNoun: 'item',
        hidden: true,
        fields: [
            { name: 'refNumber', label: 'Ref #', type: 'text', placeholder: 'INS-041 / PCE-007' },
            { name: 'type', label: 'Type', type: 'select', options: ['Inspection', 'Potential Change'] },
            { name: 'description', label: 'Description', type: 'multiline' },
            { name: 'inspector', label: 'Origin / Inspector', type: 'text' },
            { name: 'status', label: 'Status', type: 'select', options: ['Open', 'Submitted', 'Closed'] },
            { name: 'result', label: 'Result', type: 'text', placeholder: 'Pass / Fail / Pending' },
            { name: 'followUp', label: 'Follow-Up?', type: 'bool' },
            { name: 'costImpact', label: 'Est. Cost Impact ($)', type: 'number' },
            { name: 'scheduleImpact', label: 'Est. Schedule Impact', type: 'text', placeholder: '0.5 day' },
        ],
    },
];

// Wizard step order. Section steps reference sectionDefs by key.
// Safety and the Issue Log have no steps of their own — both live on the
// Setup tab (issues behind an "any issues today?" checkbox). Their rows still
// store as normal section rows, so save/review/sheet are unchanged.
// Subs live on the Lab/Eq step; photos live on Review. The Review chip shows
// no step number (WizardNav special-cases it).
export const wizardSteps = [
    { key: 'setup', title: 'Report Setup', short: 'Setup' },
    { key: 'work', title: 'Work Performed', short: 'Work' },
    { key: 'quantities', title: 'Quantities', short: 'Qty' },
    { key: 'labor', title: 'Labor & Equipment', short: 'Lab/Eq' },
    { key: 'materials', title: 'Material Deliveries', short: 'Matl' },
    { key: 'inspections', title: 'Quality', short: 'Quality' },
    { key: 'review', title: 'Review & Submit', short: 'Review' },
];

// Quick Report (/daily-report2) step order — two tabs only.
//
// Progress: the day's schedule up top (Gantt of the phases scheduled over the
// report date, with the chip picker beneath it) and the per-phase card under
// that — note, people & hours, today's quantity. That is the whole quantity
// tracking story on one screen.
//
// Equipment: the equipment matrix and the subs on site, then the filing block
// that used to be the Review tab (conditions, superintendent, the one-question
// safety block, issues/delays, the day recap, photos, notes) and the Save
// Draft / Submit buttons.
//
// Material deliveries and the quality checklist have no tab in the Quick
// Report — those sections still store and still show on the full report.
export const simpleWizardSteps = [
    { key: 'progress', title: 'Progress', short: 'Progress' },
    { key: 'equipment', title: 'Equipment & Submit', short: 'Equipment' },
];

export const sectionByKey = Object.fromEntries(sectionDefs.map((s) => [s.key, s]));

// Section keys stored generically as JSON rows (everything not typed).
export const jsonSectionKeys = sectionDefs.filter((s) => !s.typed).map((s) => s.key);
